import * as React from 'react';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import type CourseMaterials from 'pages/open-course/common/models/courseMaterials';
// eslint-disable-next-line no-restricted-imports
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';
import type Module from 'pages/open-course/common/models/module';

type ItemMetadataContextType = {
  itemMetadata: ItemMetadata;
  overrideCourseMaterials: typeof CourseMaterials | null;
  setOverrideCourseMaterials: (courseMaterials: typeof CourseMaterials | null) => void;
};

const ItemMetadataContext = createContext<ItemMetadataContextType | undefined>(undefined);

type ItemMetadataProviderProps = {
  itemMetadata: ItemMetadata;
  children: ReactNode;
};

export const ItemMetadataProvider = ({ itemMetadata, children }: ItemMetadataProviderProps) => {
  const [overrideCourseMaterials, setOverrideCourseMaterialsState] = useState<typeof CourseMaterials | null>(null);

  const setOverrideCourseMaterials = useCallback<(courseMaterials: typeof CourseMaterials | null) => void>(
    (courseMaterials) => {
      setOverrideCourseMaterialsState(courseMaterials);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      itemMetadata,
      overrideCourseMaterials,
      setOverrideCourseMaterials,
    }),
    [itemMetadata, overrideCourseMaterials, setOverrideCourseMaterials]
  );

  return <ItemMetadataContext.Provider value={contextValue}>{children}</ItemMetadataContext.Provider>;
};

export const useItemMetadata = () => {
  const context = useContext(ItemMetadataContext);
  if (!context) {
    throw new Error('useItemMetadata must be used within an ItemMetadataProvider');
  }

  const { itemMetadata, overrideCourseMaterials, setOverrideCourseMaterials } = context;

  const course = itemMetadata.get('course');
  const originalCourseMaterials = itemMetadata.get('courseMaterials') as typeof CourseMaterials;

  // Use override courseMaterials if available, otherwise use original
  const courseMaterials = overrideCourseMaterials || originalCourseMaterials;

  const modules = courseMaterials.getModules().toArray() as (typeof Module)[];
  const originalModules = originalCourseMaterials.getModules().toArray() as (typeof Module)[];
  const nextItem = courseMaterials?.getNeighbors(itemMetadata)?.nextItemMetadataOrItemGroup as ItemMetadata;
  const moduleOrderNumber = itemMetadata?.attributes?.module?.attributes?.index as number;
  const currentItemIndex: number = itemMetadata.getIndexInCourse();

  // Unfortunately, these type-assertions are not ideal. Until we migrate away from the itemMetadata model or properly type it,
  // we'll need to do this to make sure callers are getting the right types.
  return {
    itemMetadata,
    nextItem,
    courseMaterials,
    course,
    originalCourseMaterials,
    courseName: course.get('name') as string,
    courseId: course.get('id') as string,
    courseSlug: course.get('slug') as string,
    primaryLanguageCodes: course.get('primaryLanguageCodes') as string[],
    itemId: itemMetadata.get('id') as string,
    itemName: itemMetadata.get('name') as string,
    currentItemIndex,
    modules,
    originalModules,
    module: itemMetadata.get('module') as typeof Module,
    moduleName: itemMetadata.get('module').get('name') as string,
    moduleId: itemMetadata.get('module').get('id') as string,
    lessonId: itemMetadata.get('lesson').get('id') as string,
    lessonName: itemMetadata.get('lesson').get('name') as string,
    partnerName: course.get('universities').getNamesString() as string,
    videoDurationInSeconds: itemMetadata.get('timeCommitment') / 1000,
    embeddedContentSourceCourseId: itemMetadata.getEmbeddedContentSourceCourseId() as string | undefined,
    originalItemName: itemMetadata.getOriginalName() as string,
    moduleOrderNumber,
    setOverrideCourseMaterials,
  };
};
